import React, { lazy } from "react";

import {
  ArrowLeftRightFill,
  LineChartLine,
  Debts,
  AppsLine,
  Wallet3Line,
  BarChartGroupedFill,
  PieChart2Line,
  FoldersLine,
  CodeSSlashFill,
  NewspaperLine,
  PulseLine,
  KeyLine,
  FileHistoryLine,
  MapPin2Line,
  BookOpenLine,
  Settings4Line,
  UserSettingsLine,
  Building2Line,
  ShieldUserLine,
  Notification2Line,
  FileWord2Line,
  GroupLine,
  ShareLine,
} from "@datanomik/commons-ui/icons";
import { SidebarMenuLayout } from "@/layouts/SidebarMenu";
import { FinancialClosingIcon } from "@/features/financial-closing/shared/FinancialClosingIcon";
import { CashPoolingIcon } from "@/features/cash-pooling/shared/CashPoolingIcon";
import { BankIntegrationCredentialIcon } from "@/features/bank-integrations/shared/icons/BankIntegrationCredentialIcon";
import {
  OPERATOR_ROLE,
  FINANCE_ROLE,
  DEVELOPER_ROLE,
  READ_ONLY_ROLE,
  PAYMENT_CONTROLLER_ROLE,
  VIEW_FINANCE_ROLE,
} from "./AppConstants";
import { getCashPoolingRoutes } from "../features/cash-pooling/routes";
import { getTariffsRoutes } from "../features/tariffs/routes";
import { getCashFlowsV2Routes } from "../features/cash-flows/routes";
import { getDebtsRoutes } from "../features/debts/routes";

const CashFlowsPageV2 = lazy(() =>
  import("../features/cash-flows/CashFlowsPage").then((m) => ({
    default: m.default,
  })),
);

const BankIntegrationsPage = lazy(() =>
  import("@/features/bank-integrations/BankIntegrationsPage").then((m) => ({
    default: m.BankIntegrationsPage,
  })),
);

const Home = lazy(() => import("../views/modules/Home"));
const Logs = lazy(() => import("../views/modules/Logs"));
const ApiKeys = lazy(() => import("../views/modules/ApiKeys"));
const Profile = lazy(() => import("../views/modules/Profile"));
const Company = lazy(() => import("../views/modules/Company"));
const IpWhitelist = lazy(() => import("../views/modules/IpWhitelist"));
const Investments = lazy(() => import("../views/modules_v2/Investments"));
const CoePortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/CoePortfolio"),
);
const FundPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/FundPortfolio"),
);
const DepositPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/DepositPortfolio"),
);
const CommittedPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/CommittedPortfolio"),
);
const DerivativePortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/DerivativePortfolio"),
);

const Overview = lazy(() => import("../views/modules/Overview"));
const OverviewRedirect = lazy(
  () => import("../views/modules/Overview/OverviewRedirect"),
);
const ViewLink = lazy(() => import("../views/modules/Links/ViewLink"));
const TransactionsTabs = lazy(
  () => import("../views/modules/Transactions/TransactionsTabs"),
);
const NotificationsMod = lazy(() => import("../views/modules/Notifications"));
const Rules = lazy(() => import("../views/modules/Notifications/Rules"));
const RulesCreate = lazy(
  () => import("../views/modules/Notifications/Rules/RulesCreate"),
);
const CurrencyRates = lazy(() => import("../views/modules/CurrencyRates"));
const ViewAccount = lazy(() => import("../views/modules/Accounts/ViewAccount"));
const CategoryCreate = lazy(
  () => import("../views/modules/Categories/CategoryCreate"),
);
const Users = lazy(() => import("../views/modules/Users"));
const LinksScheduleRefresh = lazy(
  () => import("../views/modules/LinksScheduleRefresh"),
);
const ScheduleRefreshCreate = lazy(
  () => import("../views/modules/LinksScheduleRefresh/ScheduleRefreshCreate"),
);
const BalanceTabs = lazy(() => import("../views/modules/Balances/BalanceTabs"));
const LinkTabs = lazy(() => import("../views/modules/Links/LinkTabs"));
const GeneralOwners = lazy(() => import("../views/modules/GeneralOwners"));
const GeneralOwnersReassignOwners = lazy(
  () => import("../views/modules/GeneralOwners/ReassignOwners"),
);
const BirtReports = lazy(() => import("../views/modules/BirtReports"));
const BirtReport = lazy(() => import("../views/modules/BirtReports/Report"));
const StatementsTabs = lazy(
  () => import("../views/modules/Statements/StatementsTabs"),
);
const OptimizationWithdrawals = lazy(
  () => import("../views/modules/Optimization/Withdrawals"),
);
const Security = lazy(() => import("../views/modules/Security"));
const CashFlowCategories = lazy(
  () => import("../views/modules/CashFlowCategories"),
);
const CategoryRulesCreate = lazy(
  () => import("../views/modules/Categories/CategoryRules/RulesCreate"),
);
const ConsolidatedBalances = lazy(
  () => import("../views/modules/ConsolidatedBalances"),
);
const WorkspaceViewActions = lazy(
  () => import("../views/modules/WorkspaceViews/WorkspaceViewActions"),
);
const OnboardingWelcome = lazy(() => import("../views/modules/Onboarding"));
const OnboardingTabs = lazy(() => import("../views/modules/Onboarding/Tabs"));
const WorkspaceTabs = lazy(() => import("../views/modules/Workspaces/tabs"));
const ScheduleTabs = lazy(
  () => import("../views/modules/BirtReports/ScheduleTabs"),
);
const FinancialClosingPage = lazy(
  () => import("../features/financial-closing/FinancialClosingPage"),
);
const CashPoolingPage = lazy(
  () => import("../features/cash-pooling/CashPoolingPage"),
);
const DebtsPage = lazy(
  () => import("../features/debts/DebtsPage"),
);
const TariffsPage = lazy(
  () => import("../features/tariffs/TariffsPage"),
);

const ONBOARDING_MENU_ITEMS = [
  {
    key: "onboarding",
    path: "onboarding",
    label: "Onboarding",
    showInMenu: false,
    type: "workspace",
    children: [
      {
        key: "onboarding",
        path: "",
        label: "Onboarding",
        element: <OnboardingWelcome />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-users-profile",
        path: "users/profile",
        label: "Users",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-users-add-users",
        path: "users/add-users",
        label: "Users",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-cash-management-cash-opening-and-closing",
        path: "cash-management/cash-opening-and-closing",
        label: "Cash management",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-cash-management-liquidity",
        path: "cash-management/liquidity",
        label: "Cash management",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-cash-management-investment",
        path: "cash-management/investment",
        label: "Cash management",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-institutions",
        path: "institutions",
        label: "Banking institutions",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
    ],
  },
];
export const MAIN_MENU_ITEMS = [
  {
    key: "rules",
    label: "Rules",
    path: "rules",
    showInMenu: false,
    type: "workspace",
    children: [
      {
        key: "rules",
        path: "",
        element: <Rules />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "rules",
        path: "create",
        element: <RulesCreate />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "rules",
        path: "edit/:ruleId",
        element: <RulesCreate />,
        showInMenu: false,
        type: "workspace",
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    taskId: 1000,
    key: "overview-general",
    icon: <LineChartLine />,
    label: "Overview",
    description: "Follow your company's all financial position",
    path: "overview",
    showInMenu: true,
    withView: true,
    type: "workspace",
    children: [
      {
        key: "overview-general",
        path: "",
        element: <OverviewRedirect />,
        showInMenu: false,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1100,
        taskParentId: 1000,
        key: "overview-accounts",
        path: "accounts",
        label: "Accounts",
        element: <Overview />,
        showInMenu: false,
        hideBackButton: true,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1001,
        taskParentId: 1000,
        key: "overview-strategic",
        path: "strategic",
        label: "Strategic Treasury",
        element: <Overview />,
        showInMenu: false,
        hideBackButton: true,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1002,
        taskParentId: 1000,
        key: "overview-analytic",
        path: "analytic",
        label: "Analytical Treasury",
        element: <Overview />,
        showInMenu: false,
        hideBackButton: true,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },

  {
    taskId: 1300,
    key: "links",
    icon: <AppsLine />,
    label: "Links",
    description: "Follow the status of all your bank connections",
    path: "links",
    showInMenu: true,
    type: "workspace",
    children: [
      {
        key: "links",
        path: "",
        element: <LinkTabs />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
      },
      {
        taskId: 1400,
        taskParentId: 1300,
        key: "links-accounts",
        path: "accounts",
        label: "Accounts",
        element: <LinkTabs />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "links",
        path: "view/:id",
        element: <ViewLink />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "links-schedules",
        path: "schedules",
        label: "View schedules",
        showInMenu: false,
        type: "workspace",
        children: [
          {
            key: "links-schedules",
            path: "",
            element: <LinksScheduleRefresh />,
            showInMenu: false,
            type: "workspace",
          },
          {
            key: "links-schedules",
            path: "view/:groupId",
            element: <ScheduleRefreshCreate />,
            showInMenu: false,
            type: "workspace",
          },
        ],
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    taskId: 1500,
    key: "accounts",
    icon: <Wallet3Line />,
    label: "Cash position",
    description:
      "Clear and up-to-date view of cash positions for strategic financial management",
    path: "accounts",
    showInMenu: true,
    type: "workspace",
    withView: true,
    children: [
      {
        key: "accounts",
        path: "",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
      },
      {
        taskId: 1600,
        taskParentId: 1500,
        key: "accounts-forecast",
        path: "forecast",
        hideBackButton: true,
        element: <BalanceTabs />,
        label: "Forecast",
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1700,
        taskParentId: 1500,
        key: "accounts-history",
        path: "history",
        label: "Balance Evolution",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1700,
        taskParentId: 1500,
        key: "accounts-consolidated",
        path: "consolidated",
        label: "Consolidated view by Owner",
        element: <ConsolidatedBalances />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 1504,
        taskParentId: 1500,
        key: "accounts-manual",
        path: "accounts-manual",
        label: "Manual positions",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 1505,
        taskParentId: 1500,
        key: "account-detail",
        path: "view/:accountId",
        element: <ViewAccount />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        permissions: {
          view: ({ user }) => {
            const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
              user?.subscriptions?.currentPlan.details || {};
            return syncModes.includes("TRANSACTION");
          },
        },
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    taskId: 1817,
    key: "investments-performance-v2",
    icon: <BarChartGroupedFill />,
    label: "Investments",
    description: "Follow all about your investments",
    path: "v2/investments/performance",
    showInMenu: false,
    withView: false,
    type: "workspace",
    element: <Investments />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    taskId: 1816,
    key: "investments-v2",
    icon: <BarChartGroupedFill />,
    label: "Investments",
    description: "Follow your portfolio",
    path: "v2/investments/portfolio",
    showInMenu: true,
    withView: false,
    hideBackButton: true,
    type: "workspace",
    children: [
      {
        key: "investments-v2",
        path: "",
        element: <Investments />,
        hideBackButton: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-deposit",
        taskId: 1816,
        path: "deposit",
        element: <DepositPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-fund",
        taskId: 1816,
        path: "fund",
        element: <FundPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-coe",
        taskId: 1816,
        path: "coe",
        element: <CoePortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-committed",
        taskId: 1816,
        path: "committed",
        element: <CommittedPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-derivative",
        taskId: 1816,
        path: "derivative",
        element: <DerivativePortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    taskId: 2000,
    key: "transactions",
    icon: <ArrowLeftRightFill />,
    label: "Transactions",
    description: "Follow your financial transactions",
    path: "transactions",
    showInMenu: true,
    withView: true,
    type: "workspace",
    permissions: {
      view: ({ user }) => {
        const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
          user?.subscriptions?.currentPlan.details || {};
        return syncModes.includes("TRANSACTION");
      },
    },
    children: [
      {
        key: "transactions",
        path: "",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
      },
      {
        taskId: 2004,
        key: "pix-transactions",
        path: "pix",
        label: "Pix",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 2001,
        key: "transactions-payments",
        path: "payments",
        label: "Payments to suppliers",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 2003,
        key: "transactions-payments-receipt",
        path: "payments/receipt",
        label: "Payments receipts",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 2006,
        key: "payment-slips",
        path: "slips",
        label: "Payment Slips",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
    actions: {
      view: [
        OPERATOR_ROLE,
        FINANCE_ROLE,
        READ_ONLY_ROLE,
        PAYMENT_CONTROLLER_ROLE,
        VIEW_FINANCE_ROLE,
      ],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  // {
  //   taskId: 2200,
  //   key: "cash-flows",
  //   icon: <PieChart2Line />,
  //   label: "Cash flow",
  //   path: "cash-flows",-
  //   showInMenu: true,
  //   type: "workspace",
  //   hideBackButton: true,
  //   element: <CashFlows />,
  //   actions: {
  //     view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
  //     edit: [OPERATOR_ROLE, FINANCE_ROLE],
  //     create: [OPERATOR_ROLE, FINANCE_ROLE],
  //     export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
  //   },
  // },
  {
    taskId: 2200,
    key: "cash-flows",
    icon: <PieChart2Line />,
    label: "Cash flow",
    path: "cash-flows",
    showInMenu: true,
    type: "workspace",
    hideBackButton: true,
    element: <CashFlowsPageV2 />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getCashFlowsV2Routes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      type: "workspace",
      taskId: 2200,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        edit: [OPERATOR_ROLE, FINANCE_ROLE],
        create: [OPERATOR_ROLE, FINANCE_ROLE],
        export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    taskId: 2200,
    key: "settings-cash-flows",
    icon: <PieChart2Line />,
    label: "Cashflow Configuration",
    path: "cash-flows/settings",
    showInMenu: false,
    children: [
      {
        taskId: 2200,
        key: "groups-cash-flows",
        label: "Group of categories",
        path: "groups",
        showInMenu: false,
        type: "workspace",
        element: <CashFlowCategories />,
        permissions: {
          view: ({ user }) => {
            const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
              user?.subscriptions?.currentPlan.details || {};
            return syncModes.includes("TRANSACTION");
          },
        },
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "sections-cash-flows",
        label: "Sections",
        path: "sections",
        showInMenu: false,
        type: "workspace",
        element: <CashFlowCategories />,
        permissions: {
          view: ({ user }) => {
            const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
              user?.subscriptions?.currentPlan.details || {};
            return syncModes.includes("TRANSACTION");
          },
        },
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 2100,
        taskParentId: 2000,
        key: "categories",
        label: "Categories",
        path: "categories",
        showInMenu: false,
        type: "workspace",
        permissions: {
          view: ({ user }) => {
            const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
              user?.subscriptions?.currentPlan.details || {};
            return syncModes.includes("TRANSACTION");
          },
        },
        children: [
          {
            key: "categories",
            path: "",
            element: <CashFlowCategories />,
            showInMenu: false,
            type: "workspace",
            permissions: {
              view: ({ user }) => {
                const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
                  user?.subscriptions?.currentPlan.details || {};
                return syncModes.includes("TRANSACTION");
              },
            },
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-create",
            path: "create",
            label: "New category",
            element: <CategoryCreate />,
            showInMenu: false,
            type: "workspace",
            permissions: {
              view: ({ user }) => {
                const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
                  user?.subscriptions?.currentPlan.details || {};
                return syncModes.includes("TRANSACTION");
              },
            },
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit",
            path: "edit/:categoryId",
            element: <CategoryCreate />,
            showInMenu: false,
            type: "workspace",
            permissions: {
              view: ({ user }) => {
                const { syncModes = ["ACCOUNT", "TRANSACTION"] } =
                  user?.subscriptions?.currentPlan.details || {};
                return syncModes.includes("TRANSACTION");
              },
            },
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit-rules-create",
            path: "edit/:categoryId/rules/create",
            label: "Create new rule",
            element: <CategoryRulesCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit-rules-edit",
            path: "edit/:categoryId/rules/edit/:ruleId",
            label: "Edit rule",
            element: <CategoryRulesCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
        ],
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
  },
  {
    taskId: 4000,
    key: "reports",
    icon: <NewspaperLine />,
    label: "Reports",
    path: "reports",
    showInMenu: true,
    withView: true,
    type: "workspace",
    children: [
      {
        key: "reports",
        label: "New Report",
        path: "",
        hideBackButton: true,
        element: <BirtReports />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
        },
      },
      {
        taskId: 4000,
        key: "reports-scheduler",
        label: "Report schedules",
        path: "scheduler",
        element: <ScheduleTabs />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 4002,
        key: "reports-executions",
        label: "Executions",
        path: "executions",
        element: <ScheduleTabs />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "reports-detail",
        path: ":taskId",
        element: <BirtReport />,
        showInMenu: false,
        hiddenPageTitle: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
    },
  },
  {
    taskId: 5000,
    key: "bank-statements",
    icon: <FoldersLine />,
    label: "Bank Statements",
    path: "bank-statements",
    showInMenu: true,
    withView: true,
    type: "workspace",
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
    children: [
      {
        key: "bank-statements",
        label: "Bank Statements",
        hideBackButton: true,
        path: "",
        element: <StatementsTabs />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 5001,
        hiddenPageTitle: true,
        key: "bank-statements-my-extracts",
        path: "my-extracts",
        label: "My extracts",
        element: <StatementsTabs />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
  },
  {
    taskId: 5002,
    key: "financial-closing",
    icon: <FinancialClosingIcon />,
    label: "Fechamento financeiro",
    path: "financial-closing",
    showInMenu: true,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <FinancialClosingPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },

  {
    key: "cash-pooling",
    icon: <CashPoolingIcon />,
    taskId: 8000,
    label: "Cash Pooling",
    path: "cash-pooling",
    showInMenu: true,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <CashPoolingPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getCashPoolingRoutes().map((route) => {
      const addRouteDefaults = (r) => ({
        ...r,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        withView: true,
        taskId: 8000,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      });

      const baseRoute = addRouteDefaults(route);

      // If the route has children, apply the same properties to them recursively
      if (route.children) {
        baseRoute.children = route.children.map(addRouteDefaults);
      }

      return baseRoute;
    }),
  },
  {
    key: "debts",
    taskId: 5200,
    icon: <Debts />,
    label: "Dívidas",
    path: "debts",
    showInMenu: true,
    withView: true,
    isBeta: true,
    type: "workspace",
    hideBackButton: true,
    element: <DebtsPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getDebtsRoutes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      type: "workspace",
      withView: true,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    key: "tariffs",
    icon: <Wallet3Line />,
    taskId: 4311,
    label: "Tarifas",
    path: "tariffs",
    showInMenu: true,
    withView: true,
    isBeta: true,
    type: "workspace",
    hideBackButton: true,
    element: <TariffsPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getTariffsRoutes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      type: "workspace",
      withView: true,
      taskId: 2200,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    key: "divider-1",
    type: "divider",
    showInMenu: true,
  },
  {
    key: "developers",
    label: "Developers",
    icon: <CodeSSlashFill />,
    path: "developers",
    showInMenu: true,
    type: "workspace",
    element: <SidebarMenuLayout location="developers" />,
    children: [
      {
        key: "relevant-links-guides-docs",
        icon: <BookOpenLine />,
        label: "API docs",
        link: "https://docs.datanomik.com/docs",
        showInMenu: true,
      },
      {
        key: "api-activity",
        icon: <PulseLine />,
        label: "API Activity",
        path: "api-activity",
        element: <Home />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [DEVELOPER_ROLE],
          create: [DEVELOPER_ROLE],
          edit: [DEVELOPER_ROLE],
          delete: [DEVELOPER_ROLE],
        },
      },
      {
        key: "api-keys",
        label: "API Keys",
        description: "Generate up to 5 API Keys.",
        icon: <KeyLine />,
        hideBackButton: true,
        path: "api-keys",
        element: <ApiKeys />,
        showInMenu: true,
        type: "workspace",
        actions: {
          view: [DEVELOPER_ROLE],
          create: [DEVELOPER_ROLE],
          edit: [DEVELOPER_ROLE],
          delete: [DEVELOPER_ROLE],
        },
      },
      {
        key: "api-logs",
        icon: <FileHistoryLine />,
        label: "API Logs",
        description: "Authorized IPs",
        path: "api-logs",
        element: <Logs />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [DEVELOPER_ROLE],
        },
      },
      {
        key: "ip-whitelist",
        icon: <MapPin2Line />,
        label: "IP Whitelist",
        path: "ip-whitelist",
        hideBackButton: true,
        element: <IpWhitelist />,
        showInMenu: true,
        type: "workspace",
        actions: {
          view: [DEVELOPER_ROLE],
          create: [DEVELOPER_ROLE],
          edit: [DEVELOPER_ROLE],
          delete: [DEVELOPER_ROLE],
        },
      },
    ],
    actions: {
      view: [DEVELOPER_ROLE],
    },
  },
  {
    key: "divider-2",
    type: "divider",
    showInMenu: true,
  },
];

export const SETTINGS_MENU_ITEMS = [
  {
    key: "settings",
    path: "settings",
    label: "Settings",
    element: <SidebarMenuLayout location="settings" />,
    showInMenu: true,
    hideBackButton: true,
    icon: <Settings4Line />,
    children: [
      {
        key: "profile",
        path: "profile",
        label: "Profile",
        hideBackButton: true,
        showInMenu: true,
        icon: <UserSettingsLine />,
        children: [
          {
            key: "profile-my-profile",
            icon: <UserSettingsLine />,
            label: "My profile",
            hideBackButton: true,
            path: "",
            element: <Profile />,
            parent: "settings/profile",
          },
          {
            key: "profile-security",
            label: "Security",
            path: "security",
            hideBackButton: true,
            icon: <UserSettingsLine />,
            element: <Profile />,
            parent: "settings/profile",
          },
        ],
      },
      {
        key: "settings-company",
        label: "Company",
        path: "company",
        element: <Company />,
        showInMenu: true,
        type: "company",
        hideBackButton: true,
        icon: <Building2Line />,
        parent: "settings/company",
      },
      {
        key: "settings-general-administrators",
        icon: <Building2Line />,
        label: "General Administrators",
        path: "general-administrators",
        element: <Company />,
        showInMenu: false,
        type: "company",
        hideBackButton: true,
        parent: "settings/company",
      },
      {
        key: "settings-company-users",
        icon: <Building2Line />,
        label: "Users",
        path: "company-users",
        element: <Company />,
        showInMenu: false,
        type: "company",
        hideBackButton: true,
        parent: "settings/company",
      },
      {
        key: "settings-company-settings",
        icon: <Building2Line />,
        label: "Users",
        path: "company-settings",
        element: <Company />,
        showInMenu: false,
        type: "company",
        parent: "settings/company",
      },
      {
        key: "settings-currency-rates",
        label: "Currency rates",
        path: "currency-rates",
        element: <CurrencyRates />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "settings-notifications",
        icon: <Notification2Line />,
        label: "Notifications",
        path: "notifications",
        hideBackButton: true,
        element: <NotificationsMod />,
        showInMenu: true,
        type: "workspace",
        parent: "settings/notifications",
        children: [
          {
            key: "settings-notifications-rules",
            label: "Rules",
            path: "rules",
            element: <NotificationsMod />,
            showInMenu: false,
            icon: <Notification2Line />,
            type: "workspace",
            parent: "settings/notifications",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
            },
          },
        ],
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "settings-workspaces",
        icon: <FileWord2Line />,
        label: "Workspaces",
        path: "workspaces",
        showInMenu: true,
        type: "company",
        children: [
          {
            key: "settings-workspaces-views",
            label: "Views",
            path: "views",
            showInMenu: true,
            type: "workspace",
            children: [
              {
                key: "settings-workspaces-views",
                path: "",
                element: <WorkspaceTabs />,
                showInMenu: false,
                type: "workspace",
                icon: <FileWord2Line />,
                label: "Views",
                description:
                  "Personalize the dashboard visualization, identify trends, and make strategic decisions based on accurate and up-to-date information.",
                parent: "settings/workspaces",
              },
              {
                label: "Nova visualização",
                icon: <FileWord2Line />,
                key: "settings-workspaces-views-create",
                path: "create",
                element: <WorkspaceViewActions />,
                showInMenu: false,
                type: "workspace",
                parent: "settings/workspaces",
              },
              {
                key: "settings-workspaces-users",
                label: "Users",
                path: ":workspaceId/users",
                icon: <GroupLine />,
                element: <Users />,
                showInMenu: false,
                type: "workspace",
                parent: "settings/workspaces",
              },
              {
                key: "settings-workspaces-views-mine",
                label: "My views",
                path: "mine",
                withView: true,
                element: <WorkspaceTabs />,
                showInMenu: false,
                type: "workspace",
                icon: <FileWord2Line />,
                parent: "settings/workspaces",
                actions: {
                  view: [
                    OPERATOR_ROLE,
                    FINANCE_ROLE,
                    READ_ONLY_ROLE,
                    DEVELOPER_ROLE,
                  ],
                },
              },
              {
                label: "Edit view",
                key: "settings-workspaces-views-edit",
                path: ":workspaceViewId/edit",
                icon: <GroupLine />,
                element: <WorkspaceViewActions />,
                showInMenu: false,
                type: "workspace",
              },
            ],
          },
          {
            key: "settings-workspaces",
            path: "",
            element: <WorkspaceTabs />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces",
            description: "Check out the workspaces already available.",
            icon: <FileWord2Line />,
            label: "Workspaces",
          },
          {
            key: "settings-workspaces-mine",
            label: "My workspaces",
            path: "mine",
            withView: true,
            icon: <FileWord2Line />,
            element: <WorkspaceTabs />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/workspaces",
            actions: {
              view: [
                OPERATOR_ROLE,
                FINANCE_ROLE,
                READ_ONLY_ROLE,
                DEVELOPER_ROLE,
              ],
            },
          },
          {
            key: "settings-workspaces-users",
            label: "Users",
            path: ":workspaceId/users",
            element: <Users />,
            hideBackButton: true,
            icon: <GroupLine />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/workspaces",
          },
        ],
      },
      {
        key: "settings-general-owners",
        label: "Owners",
        icon: <ShareLine />,
        path: "general-owners",
        hideBackButton: true,
        showInMenu: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
        children: [
          {
            key: "settings-general-owners-accounts",
            path: "accounts",
            hideBackButton: true,
            element: <GeneralOwners />,
            showInMenu: false,
            type: "workspace",
            label: "Owners",
            icon: <ShareLine />,
            parent: "settings/general-owners",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "settings-general-owners",
            path: "",
            element: <GeneralOwners />,
            showInMenu: false,
            hideBackButton: true,
            type: "workspace",
            label: "Owners",
            icon: <ShareLine />,
            parent: "settings/general-owners",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "settings-general-owners",
            path: ":generalOwnerId/reassign-owners",
            element: <GeneralOwnersReassignOwners />,
            showInMenu: false,
            type: "workspace",
            label: "Reassign General Owner",
            parent: "settings/general-owners",
            icon: <ShareLine />,
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
        ],
      },
      {
        key: "settings-users",
        label: "Users",
        path: "workspaces/users",
        element: <Users />,
        hideBackButton: true,
        icon: <GroupLine />,
        showInMenu: true,
        type: "workspace",
        parent: "settings/workspaces/users",
      },
      {
        key: "settings-security",
        icon: <ShieldUserLine />,
        label: "Security",
        path: "password-policy",
        element: <Security />,
        showInMenu: false,
        type: "company",
        parent: "settings/login-logs",
      },
      {
        key: "user-login-logs",
        icon: <ShieldUserLine />,
        label: "Security",
        path: "login-logs",
        element: <Security />,
        showInMenu: true,
        type: "company",
        parent: "settings/login-logs",
      },
      {
        key: "settings-bank-integrations",
        icon: <BankIntegrationCredentialIcon />,
        label: "Bank Credentials",
        description: "Your credentials are encrypted and stored securely.",
        path: "bank-integrations",
        element: <BankIntegrationsPage />,
        hideBackButton: true,
        showInMenu: true,
        type: "workspace",
        parent: "settings/bank-integrations",
      },
    ],
  },
];

export const APP_ROUTES = [
  {
    path: "/:workspace",
    children: [
      ...MAIN_MENU_ITEMS,
      ...SETTINGS_MENU_ITEMS,
      ...ONBOARDING_MENU_ITEMS,
    ],
  },
];
